
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface StreakUser {
  userId: string;
  userName: string;
  avatar: string;
  currentStreak: number;
  rank: number;
}

interface StudyStreakRankingWidgetProps {
  layoutMode?: 'normal' | 'compact';
}

export default function StudyStreakRankingWidget({ layoutMode = 'normal' }: StudyStreakRankingWidgetProps) {
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const [rankings, setRankings] = useState<StreakUser[]>([]);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStreakRankings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchStreakRankings = async () => {
    try {
      setLoading(true);

      const { data: profiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, username');

      if (profileError) throw profileError;

      const streakPromises = (profiles || []).map(async (profile) => {
        const { data: diaries } = await supabase
          .from('diary_entries')
          .select('created_at')
          .eq('user_id', profile.user_id)
          .order('created_at', { ascending: false });

        if (!diaries || diaries.length === 0) {
          return {
            userId: profile.user_id,
            userName: profile.username || 'ユーザー',
            avatar: '',
            currentStreak: 0,
            rank: 0,
          };
        }

        const dailyDates = new Set<string>();
        diaries.forEach((diary) => {
          const date = new Date(diary.created_at).toISOString().split('T')[0];
          dailyDates.add(date);
        });

        let currentStreak = 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const todayStr = today.toISOString().split('T')[0];
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        const startFrom = dailyDates.has(todayStr) ? todayStr : yesterdayStr;
        if (dailyDates.has(startFrom)) {
          const checkDate = new Date(startFrom);
          while (true) {
            const dateStr = checkDate.toISOString().split('T')[0];
            if (dailyDates.has(dateStr)) {
              currentStreak++;
              checkDate.setDate(checkDate.getDate() - 1);
            } else {
              break;
            }
          }
        }

        return {
          userId: profile.user_id,
          userName: profile.username || 'ユーザー',
          avatar: '',
          currentStreak,
          rank: 0,
        };
      });

      const streakUsers = await Promise.all(streakPromises);

      streakUsers.sort((a, b) => b.currentStreak - a.currentStreak);

      let rankCounter = 1;
      streakUsers.forEach((streakUser, index) => {
        if (index > 0 && streakUsers[index - 1].currentStreak !== streakUser.currentStreak) {
          rankCounter = index + 1;
        }
        streakUser.rank = rankCounter;
      });

      const top5 = streakUsers.slice(0, 5);
      setRankings(top5);

      if (user) {
        const myData = streakUsers.find((s) => s.userId === user.id);
        if (myData) {
          setMyRank(myData.rank);
        }
      }
    } catch (error) {
      console.error('連続学習日数ランキングの取得に失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${layoutMode === 'compact' ? 'p-4' : 'p-6'}`}>
      <h3 className={`font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2 ${layoutMode === 'compact' ? 'text-base' : 'text-lg'}`}>
        <i className="ri-fire-line text-orange-500"></i>
        ストリークランキング
      </h3>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      ) : (
        <div className={`space-y-3 ${layoutMode === 'compact' ? 'text-sm' : ''}`}>
          {rankings.map((rankUser, index) => (
            <div
              key={rankUser.userId}
              className={`flex items-center gap-3 ${layoutMode === 'compact' ? 'p-2' : 'p-3'} ${
                rankUser.userId === currentUserId
                  ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800'
                  : 'bg-gray-50 dark:bg-gray-700/50'
              } rounded-lg`}
            >
              <div
                className={`font-bold ${
                  index === 0
                    ? 'text-yellow-500'
                    : index === 1
                    ? 'text-gray-400'
                    : index === 2
                    ? 'text-orange-600'
                    : 'text-gray-500'
                } ${layoutMode === 'compact' ? 'w-6 text-center' : 'w-8 text-center text-lg'}`}
              >
                {index < 3 ? <i className="ri-trophy-fill"></i> : index + 1}
              </div>

              <div
                className={`${layoutMode === 'compact' ? 'w-8 h-8' : 'w-10 h-10'} rounded-full bg-gradient-to-br from-orange-400 to-red-600 flex items-center justify-center text-white font-bold flex-shrink-0`}
              >
                {rankUser.userName.charAt(0)}
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 dark:text-white truncate">
                  {rankUser.userName}
                </p>
                <p className={`text-gray-500 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>
                  {rankUser.currentStreak}日連続
                </p>
              </div>

              {rankUser.userId === currentUserId && (
                <span
                  className={`bg-orange-600 text-white font-medium rounded-full flex-shrink-0 ${
                    layoutMode === 'compact' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'
                  }`}
                >
                  あなた
                </span>
              )}
            </div>
          ))}

          {rankings.length === 0 && (
            <div className="text-center py-8">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className="ri-fire-line text-4xl text-gray-300 dark:text-gray-600"></i>
              </div>
              <p className="text-gray-500 dark:text-gray-400">ランキングデータがありません</p>
            </div>
          )}

          {myRank !== null && myRank > 5 && (
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className={`text-gray-500 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>
                あなたの順位：<span className="font-bold text-orange-500">{myRank}位</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
